import React, { useState } from "react";

import type { ProcessedSameEventMultiSelection } from "@/hooks/firestore/betting/useEntries";
import type { OddsFormat } from "types/BetTypes";

import cx from "classnames";
import { titleCase } from "title-case";

import { SGMIcon, SRMIcon } from "@picklebet/ui";
import { useIsParlayLocale } from "@/hooks/useMultiSlug";

import { usePreferences } from "@/hooks/firestore/user/usePreferences";
import { BetOddsWithDeductions } from "@/features/entries/components/BetOddsWithDeductions";

import {
  getSelectionParentName,
  getSelectionStatus,
  selectionToUrl,
  sortSgmOutcomes,
  sortSrmOutcomes,
} from "@/features/entries/util/selection-helpers";
import { MoreInfo } from "@picklebet/ui";
import { Drawer } from "@picklebet/ui";
import { formatUserDateTime } from "@/utilities/datetime";
import { SELECTION_DATETIME } from "@/utilities/dateTimeFormats";
import { SameEventMultiOutcome } from "../outcomes/SameEventMultiOutcome";
import { LegStatus } from "../bet-tracker/LegStatus";
import { ScoreBoard } from "../bet-tracker/ScoreBoard";
import { DrawerLineItem } from "../common/DrawerLineItem";
import { TeamNames } from "../common/TeamNames";
import { ComplexBetPill } from "./ComplexBetPill";
import { isOutright } from "@/utilities/sharedBettingUtilities";
import { parseISO } from "date-fns";
import { Countdown } from "@/features/shell/components/shared/Countdown";
import { useTrackerEnabledSports } from "@/features/entries/hooks/useTrackerEnabledSports";

import * as styles from "./SameEventMultiSelection.module.scss";

interface SameEventMultiSelectionProps {
  entryId: number;
  isShared: boolean;
  selection: ProcessedSameEventMultiSelection;
  isFirst?: boolean;
  isLast?: boolean;
  isEntryWon?: boolean;
  hasMultipleSelections?: boolean;
  createdAt: string;
}

const SameEventMultiSelection = ({
  entryId,
  isShared,
  selection,
  isFirst,
  isLast,
  isEntryWon,
  hasMultipleSelections,
  createdAt,
}: SameEventMultiSelectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const { loading: trackableSportsLoading, enabledSports } =
    useTrackerEnabledSports();

  const isSRM = selection.event.eventType === "RACE";
  const isParlayLocale = useIsParlayLocale();
  const parentName = getSelectionParentName(selection);
  const eventName = selection.event.eventName;
  const oddsFormat =
    usePreferences()?.preferences?.oddsFormat?.toLowerCase() as OddsFormat;
  const legsCount = Object.keys(selection.outcomes).length;

  const hasTrackableBets =
    !isSRM &&
    Object.values(selection.outcomes).some(
      (outcome) => !!Object.keys(outcome.winCriteria ?? {}).length,
    );

  const isSelectionOutright = isOutright(selection.event.eventType);

  const startTime =
    isSelectionOutright && selection.marketNextBetStop
      ? selection.marketNextBetStop
      : selection.event.scheduledStartTime;

  const outcomes = isSRM
    ? sortSrmOutcomes(selection.outcomes ?? {})
    : sortSgmOutcomes(selection.outcomes ?? {});

  const trackerDisabled =
    !trackableSportsLoading && enabledSports[selection.event.sport] === false;

  const oddsDisplay = (
    <>
      <span>{legsCount} legs </span>
      <BetOddsWithDeductions
        prefix={"@"}
        base={selection.odds}
        format={oddsFormat}
        settlementOdds={selection.settlementOdds}
        deadHeatFactor={selection.deadHeatFactor}
        deductions={selection.deductions}
        isVoid={selection.void}
        pickUrl={selectionToUrl(selection)}
      />
    </>
  );

  return (
    <div
      className={cx(styles.selection, {
        [styles.isLast]: isLast,
        [styles.hasMultipleSelections]: hasMultipleSelections && !isFirst,
        [styles.isWon]: isEntryWon,
      })}
    >
      <div className={styles.scoreboardAndLegs}>
        <ScoreBoard
          selection={selection}
          hasTrackableBets={hasTrackableBets}
          trackerDisabled={trackerDisabled}
        />

        {hasMultipleSelections && (
          <ComplexBetPill
            icon={
              isSRM ? (
                <SRMIcon isParlayLocale={isParlayLocale} />
              ) : (
                <SGMIcon isParlayLocale={isParlayLocale} />
              )
            }
            odds={oddsDisplay}
            isWon={isEntryWon}
          />
        )}

        <div className={styles.legsAndMoreInfo}>
          <LegStatus selection={selection} isEntryWon={isEntryWon} />
          <div className={styles.caret}>
            <MoreInfo isExpanded={isExpanded} onClick={toggleExpanded} />
          </div>
        </div>
        <Drawer isOpen={isExpanded}>
          <div className={styles.drawer}>
            <DrawerLineItem
              label="Start"
              value={formatUserDateTime(
                selection.event.scheduledStartTime!,
                SELECTION_DATETIME,
              )}
            />

            {isSRM ? (
              <DrawerLineItem
                label="Event"
                value={titleCase(
                  selection.event.eventName?.toLowerCase() ?? "",
                )}
              />
            ) : (
              <DrawerLineItem
                label="Match"
                value={<TeamNames eventName={eventName} />}
              />
            )}

            <DrawerLineItem label="" value={parentName} />

            <DrawerLineItem
              label="Placed"
              value={formatUserDateTime(createdAt, SELECTION_DATETIME)}
            />

            {!isShared && (
              <DrawerLineItem label="Entry Id" value={`B-${entryId}`} />
            )}
          </div>
        </Drawer>
      </div>

      {outcomes.map((outcome) => (
        <SameEventMultiOutcome
          key={`${outcome.marketId}-${outcome.outcomeId}`}
          outcome={outcome}
          eventId={selection.event.eventId}
          sport={selection.event.sport}
          isWon={!!isEntryWon}
          trackerDisabled={trackerDisabled}
          isRace={selection.event.eventType === "RACE"}
          silksUrl={selection.event.attributes?.silksUrl ?? ""}
          status={getSelectionStatus(selection, outcome.outcomeResult)}
          countdown={
            <Countdown
              scheduledStartTime={parseISO(startTime ?? "")}
              selection={selection}
            />
          }
        />
      ))}
    </div>
  );
};

export { SameEventMultiSelection };
